import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Info, Star, Zap } from "lucide-react";
import { RevealLines } from "@/components/Reveal";
import { resolveMedia } from "@/lib/api";

export const Hero = ({ featured, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const posterY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const main = featured?.[0];
  const side = featured?.slice(1, 3) || [];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        {main && (
          <img src={resolveMedia(main.banner_url || main.cover_url)} alt="" className="w-full h-[120%] object-cover opacity-55" />
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#08070C] via-[#08070C]/70 to-[#08070C]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#08070C] via-transparent to-transparent" />
      <div className="absolute inset-0 kp-scanlines" />
      <div className="absolute -left-20 top-1/3 w-[40rem] h-[40rem] rounded-full bg-[#FF2A55]/10 blur-[140px]" />
      <div className="absolute right-0 top-10 w-[30rem] h-[30rem] rounded-full bg-[#00F0FF]/[0.07] blur-[140px]" />

      <motion.div style={{ opacity: fade }} className="relative max-w-[1400px] mx-auto w-full px-5 sm:px-8 pb-20 pt-40 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7 flex flex-col gap-7">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1, duration: 0.7 }} className="flex items-center gap-3">
            <span className="relative w-2.5 h-2.5 rounded-full bg-[#FF2A55] kp-pulse" />
            <span className="kp-eyebrow">Featured · {total} titles</span>
          </motion.div>

          <RevealLines
            testId="hero-title"
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight"
            lines={main ? [main.title] : [""]}
          />

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="flex flex-wrap items-center gap-4">
            {main && (
              <Link to={`/watch/${main.id}/1`} data-testid="hero-play-button" className="kp-btn kp-btn-primary">
                <Play size={14} className="fill-current" /> Watch now
              </Link>
            )}
            {main && (
              <Link to={`/anime/${main.id}`} data-testid="hero-details-button" className="kp-btn kp-btn-ghost">
                <Info size={14} /> Details
              </Link>
            )}
          </motion.div>

          {main && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="flex items-center gap-6 text-xs font-mono text-[#94A3B8]">
              <span className="flex items-center gap-1.5"><Star size={12} className="text-[#00F0FF] fill-[#00F0FF]" /> {main.rating}</span>
              <span>{main.year}</span>
              <span className="text-[#FF2A55]">{main.status?.toUpperCase()}</span>
              <span className="hidden sm:inline">{main.genres?.join(" / ")}</span>
            </motion.div>
          )}
        </div>

        <motion.div style={{ y: posterY }} className="lg:col-span-5 hidden lg:grid grid-cols-2 gap-4 items-end">
          {main && (
            <motion.div initial={{ opacity: 0, y: 60, rotate: -3 }} animate={{ opacity: 1, y: 0, rotate: -3 }} transition={{ delay: 0.5, duration: 1, ease: [0.2, 0.8, 0.2, 1] }} className="col-span-1 row-span-2 relative aspect-[3/4] kp-card-clip overflow-hidden border border-[#FF2A55]/40 kp-glow-crimson">
              <img src={resolveMedia(main.cover_url)} alt={main.title} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#08070C] to-transparent">
                <span className="kp-eyebrow !text-[0.55rem] flex items-center gap-1"><Zap size={10} /> Featured</span>
              </div>
            </motion.div>
          )}
          {side.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + i * 0.15, duration: 1, ease: [0.2, 0.8, 0.2, 1] }}>
              <Link to={`/anime/${a.id}`} data-testid={`hero-side-card-${a.id}`} className="block relative aspect-[4/3] kp-card-clip overflow-hidden border border-white/10 hover:border-[#00F0FF]/60 transition-colors group">
                <img src={resolveMedia(a.cover_url)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08070C] to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 font-display text-xs font-semibold truncate">{a.title}</div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 kp-eyebrow !text-[0.55rem] flex flex-col items-center gap-2">
        <span className="w-px h-10 bg-gradient-to-b from-[#00F0FF] to-transparent" />
        Scroll
      </motion.div>
    </section>
  );
};
